/** The artifact merchant: an adventure object that sells what it is holding.
 *
 * The kernel's `BUY_ARTIFACT` names an object and an artifact, and the object's
 * payload is what says which artifact and at what price - `price` when the
 * object sets one, the artifact's own `cost` when it does not. That fallback is
 * the kernel's (`buyArtifact` in `adventure/execute.ts`), so the host resolves
 * the offer and this screen only draws it: an offer priced here would be a
 * second implementation of the rule and would eventually quote a number the
 * kernel then refuses.
 *
 * Refusals are the kernel's own words. A player who cannot afford a Sword of
 * Hellfire is told what it costs and what they hold, because that is what
 * `INSUFFICIENT_RESOURCES` carries.
 */

import type {
  ArtifactDefId, ContentRegistry, GameCommand, HeroState, ObjectId, Reason, Resources,
} from "@game/core";
import { RESOURCE_ICON } from "../theme/art.js";
import { artifactIcon } from "./art.js";
import { groupDigits, titleCase } from "./format.js";
import { ART_ICON_LARGE, ICON_SMALL, ReasonLine } from "./parts.js";
import { MaybeIcon, ScreenDialog, ScreenStyles } from "./primitives.js";
import { explainReason } from "./townModel.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/** One thing on the merchant's counter. Resolved by the host from the object's
 *  payload; nothing here derives a price. */
export interface ArtifactOffer {
  readonly artifact: ArtifactDefId;
  readonly price: number;
}

export interface ArtifactMerchantProps {
  readonly content: ContentRegistry;
  readonly object: ObjectId;
  /** The shop's own name, from the map-object definition. */
  readonly title: string;
  readonly hero: HeroState;
  readonly treasury: Resources;
  readonly offers: readonly ArtifactOffer[];
  /** From the kernel: null means "this purchase would go through". */
  readonly buyReason: (artifact: ArtifactDefId) => Reason | null;
  readonly onCommand: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}

export function ArtifactMerchant(props: ArtifactMerchantProps): JSX.Element {
  useMerchantStyles();
  const { content } = props;
  return (
    <ScreenDialog title={props.title} onClose={props.onClose} width={640}>
      {/* This screen opens straight over the map, with no town or hero sheet
          under it to have mounted the stylesheet. Without this the dialog draws
          as unstyled text in the top-left corner - which is what it did. */}
      <ScreenStyles />
      <div className="hs-tiny hs-muted" style={{ marginBottom: "10px" }}>
        {`${content.heroes.get(props.hero.def)?.name ?? "Your hero"}`
          + ` is carrying ${groupDigits(props.treasury.gold)} gold.`}
      </div>

      {props.offers.length === 0 ? (
        <div className="hs-card hs-stone hs-tiny hs-muted" data-qa="merchant-empty">
          The counter is bare. Nothing is for sale here.
        </div>
      ) : (
        <div className="hs-list">
          {props.offers.map((offer) => {
            const def = content.artifacts.get(offer.artifact);
            const reason = props.buyReason(offer.artifact);
            const legal = reason === null;
            return (
              <div
                className="hs-card hm-shop__offer"
                key={offer.artifact as string}
                data-qa="merchant-offer"
                style={{ opacity: legal ? 1 : 0.55 }}
              >
                <div className="hs-row" style={{ gap: "10px" }}>
                  {/* The piece sits in a cut socket the way it does in the
                      hero's backpack, rather than floating on the docket. */}
                  <span className="hm-shop__socket">
                    <MaybeIcon
                      atlasKey={def === undefined ? null : artifactIcon(def)}
                      size={ART_ICON_LARGE}
                      title={def?.name ?? "Artifact"}
                    />
                  </span>
                  <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <span className="hs-listrow__name" style={{ display: "block" }}>
                      {def?.name ?? titleCase(offer.artifact as string)}
                    </span>
                    <span className="hs-listrow__meta">
                      {def === undefined ? "" : `${titleCase(def.rank)} · ${titleCase(def.slot)}`}
                    </span>
                  </span>
                  <span className="hs-cost__item hs-cost__item--ok">
                    <MaybeIcon atlasKey={RESOURCE_ICON.gold} size={ICON_SMALL} title="gold" />
                    <span className="hs-num">{groupDigits(offer.price)}</span>
                  </span>
                  <button
                    type="button"
                    className="hs-btn hm-shop__buy"
                    disabled={!legal}
                    title={reason === null
                      ? `Buy ${def?.name ?? "it"}`
                      : explainReason(content, reason)}
                    onClick={() => {
                      props.onCommand({
                        type: "BUY_ARTIFACT",
                        hero: props.hero.id,
                        object: props.object,
                        artifact: offer.artifact,
                      });
                    }}
                  >
                    Buy
                  </button>
                </div>
                {def === undefined ? null : (
                  <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>{def.description}</div>
                )}
                <ReasonLine content={content} reason={reason} lead="Cannot buy:" />
              </div>
            );
          })}
        </div>
      )}
    </ScreenDialog>
  );
}

// ------------------------------------------------------------------ styles

const SHOP_STYLE_ID = "hm-artifact-merchant";

/* The counter was a cold blue-grey slab inside a wood-and-brass dialog - the
   one surface on the screen that belonged to a different game. A merchant's
   offer is a docket: parchment, ink, and the piece set into a cut socket. */
const SHOP_CSS = `
.hs-card.hm-shop__offer {
  color: #2c2008;
  background-color: #dcc697;
  background-image:
    repeating-linear-gradient(91deg, rgba(120,92,48,0.05) 0 1px, rgba(0,0,0,0) 1px 4px),
    radial-gradient(120% 140% at 50% 8%, #f2e3bd 0%, #dcc697 52%, #b79f72 100%);
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.75),
    inset 0 0 26px rgba(120,88,42,0.25),
    0 2px 6px rgba(0,0,0,0.45);
}
.hs-card.hm-shop__offer .hs-listrow__name {
  font-size: max(16px, var(--hm-type-floor)); color: #1d1406; text-shadow: 0 1px 0 rgba(255,248,222,0.55);
}
.hs-card.hm-shop__offer .hs-listrow__meta { color: #5b4520; }
.hs-card.hm-shop__offer .hs-tiny,
.hs-card.hm-shop__offer .hs-muted { color: #4a3a1c; }
.hs-card.hm-shop__offer .hs-num { color: #2c2008; text-shadow: 0 1px 0 rgba(255,248,222,0.6); }
/* Struck bronze. The shared gold button paints a dark-gold label on a gold
   face, which left BUY barely readable against its own background. */
.hs-btn.hm-shop__buy {
  padding: 7px 20px 8px;
  border: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(13px, var(--hm-type-floor));
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #fff2cd;
  background-color: #6a4d1c;
  background-image:
    radial-gradient(80% 62% at 30% 10%, rgba(255,246,220,0.48) 0%, rgba(255,246,220,0) 60%),
    linear-gradient(172deg, #b8913f 0%, #7d5c22 52%, #402e0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,26,6,0.9),
    inset 0 2px 0 rgba(255,246,220,0.6),
    inset 0 -3px 6px rgba(0,0,0,0.5),
    0 1px 0 rgba(255,252,235,0.6),
    0 3px 7px rgba(0,0,0,0.45);
  text-shadow: 0 -1px 0 rgba(0,0,0,0.6), 0 1px 0 rgba(255,236,186,0.28);
  clip-path: polygon(8px 0%, 100% 0%, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0% 100%, 0% 8px);
}
.hs-btn.hm-shop__buy:hover:not(:disabled) { filter: brightness(1.14) saturate(1.05); }
.hs-btn.hm-shop__buy:disabled {
  cursor: not-allowed;
  color: #8a7854;
  background-image: linear-gradient(172deg, #6d6048 0%, #4f4433 52%, #2e2619 100%);
  box-shadow: inset 0 0 0 1px rgba(38,30,10,0.75), inset 0 2px 0 rgba(255,244,212,0.18);
  text-shadow: 0 1px 0 rgba(0,0,0,0.35);
}
.hm-shop__socket {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  background-color: #3c2d12;
  background-image: linear-gradient(180deg, #241a08 0%, #4a3617 60%, #5c4520 100%);
  box-shadow:
    inset 0 2px 0 0 #1a1206,
    inset 0 -2px 0 0 #7a5c2c,
    inset 2px 0 0 0 #3a2a10,
    inset -2px 0 0 0 #3a2a10,
    inset 0 4px 8px rgba(0,0,0,0.45),
    0 1px 0 rgba(255,226,170,0.16);
}
`;

function useMerchantStyles(): void {
  useStyleSheet(SHOP_STYLE_ID, SHOP_CSS, StyleLayer.screen);
}
